import "./ViewProductOrder.scss";
import ColorButton from "../../../components/ColorButton/ColorButton";
import Avatr from "./../../../assets/avatar.svg";
import Location from "./../../../assets/location-icon.png";
import PrintIcon from "@mui/icons-material/Print";
import DirectionsIcon from "@mui/icons-material/Directions";
import { DataGrid } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import Stepper from "../../../components/Stepper/Stepper";
import Phone from "@mui/icons-material/LocalPhoneOutlined";
import LocationIcon from "@mui/icons-material/LocationOnOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlined";
import CalendarIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTimeOutlined";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import { useParams } from "react-router-dom";
import api from "../../../utils/api";
import Loader from "../../../components/Loader";
import moment from "moment";
import PrintBillModal from "../../../components/PrintBillModal/PrintBillModal";
import LocationMapModal from "../../../components/LocationMapModal/LocationMapModal";
import { SaudiArabiaAreas } from "../../../SourceData";

const ViewProductOrder = () => {
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [openPrintModal, setOpenPrintModal] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const { id } = useParams();

  // API base URL for images
  const PHOTOS_API =
    process.env.REACT_APP_PHOTOS_API || "https://your-api-url.com/";

  // Default placeholder image
  const DEFAULT_IMAGE =
    "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM=";

  // DataGrid columns for services
  const OrderColumns = [
    {
      field: "index",
      headerName: "#",
      minWidth: 40,
      headerAlign: "center",
      align: "center",
      sortable: false,
    },
    {
      field: "name",
      headerName: "اسم الخدمة",
      minWidth: 160,
      headerAlign: "center",
      align: "center",
      flex: 1,
      renderCell: (params) => {
        return (
          params?.row?.service?.name?.ar || params?.row?.name?.ar || "غير متوفر"
        );
      },
    },
    {
      field: "quantity",
      headerName: "الكمية",
      minWidth: 100,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        return params?.row?.quantity || 1;
      },
    },
    {
      field: "price",
      headerName: "سعر الخدمة",
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const price = params?.row?.service?.price || params?.row?.price || 0;
        return `${price} ريال`;
      },
    },
    {
      field: "total",
      headerName: "الإجمالي",
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const price = params?.row?.service?.price || params?.row?.price || 0;
        const quantity = params?.row?.quantity || 1;
        const total = price * quantity;
        return `${total} ريال`;
      },
    },
  ];

  useEffect(() => {
    getOrderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getOrderData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/bookings/${id}`);

      if (response.data) {
        setOrder(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintClick = () => {
    setOpenPrintModal(true);
  };

  const handleClosePrintModal = () => {
    setOpenPrintModal(false);
  };

  const handleLocationClick = () => {
    setSelectedAddress(order?.bookingAddress);
    setMapModalOpen(true);
  };

  // Replace the getDirections function with this corrected version
  const getDirections = () => {
    // Get coordinates from bookingAddress
    const coordinates = order?.bookingAddress?.coordinates?.coordinates;

    if (!coordinates || coordinates.length !== 2) {
      alert("عذراً، الموقع غير متوفر");
      return;
    }

    // coordinates format is [longitude, latitude]
    const [lng, lat] = coordinates;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const origin = `${position.coords.latitude},${position.coords.longitude}`;
          const dest = `${lat},${lng}`;
          const url = `https://www.google.com/maps/dir/${origin}/${dest}`;
          window.open(url, "_blank");
        },
        (error) => {
          console.error("Geolocation error:", error);
          // Fallback to just showing destination
          const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
          window.open(url, "_blank");
        }
      );
    } else {
      // Fallback to just showing destination
      const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      window.open(url, "_blank");
    }
  };

  // Helper function to get payment method text
  const getPaymentMethod = () => {
    if (order?.isPackage) {
      return "عن طريق الاشتراك";
    }
    return order?.paymentMethod || "غير محدد";
  };

  // Helper function to get status image
  const getStatusImage = (status) => {
    const statusImage = order?.statusImages?.find(
      (img) => img.status === status
    );
    return statusImage?.image
      ? `${PHOTOS_API}${statusImage.image}`
      : DEFAULT_IMAGE;
  };

  // Helper function to get status label in Arabic
  const getStatusLabel = (status) => {
    const statusLabels = {
      booked: "تم الحجز",
      onTheWay: "في الطريق",
      technicianArrived: "وصل الفني",
      inProgress: "قيد التنفيذ",
      completed: "مكتمل",
      cancelled: "ملغي",
    };
    return statusLabels[status] || status;
  };

  const getAreaName = (areaId) => {
    const area = SaudiArabiaAreas.find((a) => a.areaId === areaId);
    return area ? area.areaNameArabic : areaId;
  };

  // Get governorate name by ID
  const getGovernorateName = (govId) => {
    // Search through all areas to find the governorate
    for (const area of SaudiArabiaAreas) {
      const gov = area.governorates?.find((g) => g.id === govId);
      if (gov) {
        return gov.nameArabic;
      }
    }
    return govId; // Return the ID if not found
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="view-product-order">
          <div className="header">
            <div className="right">
              <div className="order-info">
                <div className="info">
                  <h3>رقم الطلب: {order?.orderNumber || "غير متوفر"}</h3>
                  <p>
                    {order?.createdAt
                      ? moment(order.createdAt)
                          .locale("ar")
                          .format("DD/MM/YYYY - hh:mm A")
                      : "غير متوفر"}
                  </p>
                  <span>طريقة الدفع: {getPaymentMethod()}</span>
                </div>

                {/* Hide total price if it's a package */}
                {!order?.isPackage && (
                  <div className="btn">
                    <div className="admin-share">
                      <span>اجمالي تكلفة الطلب</span>
                      <p>{order?.totalPrice?.totalPrice || 0} ريال</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="user-info">
                <div className="user">
                  <p className="title">معلومات المستخدم</p>
                  <div className="user-container">
                    <div className="info">
                      <img src={Avatr} alt="avatar" />
                      <div className="wrapper">
                        <p>{order?.user?.name || "غير متوفر"}</p>
                        <span>
                          <Phone className="icon" />
                          {order?.user?.phoneNumber || "غير متوفر"}
                        </span>

                        <span>
                          <LocationIcon
                            className="icon"
                            onClick={handleLocationClick}
                            style={{ cursor: "pointer" }}
                          />
                          عنوان الطلب:{" "}
                          {order?.bookingAddress?.governorate
                            ? getGovernorateName(
                                order.bookingAddress.governorate
                              )
                            : ""}
                          {order?.bookingAddress?.area &&
                          order?.bookingAddress?.governorate
                            ? " - "
                            : ""}
                          {order?.bookingAddress?.area
                            ? getAreaName(order.bookingAddress.area)
                            : ""}
                          {(order?.bookingAddress?.governorate ||
                            order?.bookingAddress?.area) &&
                          order?.bookingAddress?.addressSign
                            ? " - "
                            : ""}
                          {order?.bookingAddress?.addressSign || "غير متوفر"}
                        </span>
                      </div>
                    </div>
                    <img
                      src={Location}
                      alt="location"
                      onClick={handleLocationClick}
                      style={{ cursor: "pointer" }}
                    />
                  </div>

                  {/* Directions Button */}
                  <div className="directions-btn" style={{ marginTop: "12px" }}>
                    <ColorButton
                      size="large"
                      color="#4CAF50"
                      text="الاتجاه إلى الموقع"
                      icon={<DirectionsIcon />}
                      onClick={getDirections}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Invoice or Package Info */}
            <div className="left">
              {order?.isPackage ? (
                // Package Info Card
                <div className="total package-info">
                  <p className="title">معلومات الاشتراك</p>

                  <div className="item">
                    <span>
                      <DescriptionIcon className="item-icon" />
                      نوع الطلب
                    </span>
                    <span className="highlight-text">طلب اشتراك</span>
                  </div>

                  {order?.technician && (
                    <div className="item">
                      <span>
                        <PersonIcon className="item-icon" />
                        الفني المسؤول
                      </span>
                      <span>{order.technician.name}</span>
                    </div>
                  )}

                  {order?.date && (
                    <div className="item">
                      <span>
                        <CalendarIcon className="item-icon" />
                        تاريخ الزيارة
                      </span>
                      <span>
                        {moment(order.date).locale("ar").format("DD/MM/YYYY")}
                      </span>
                    </div>
                  )}

                  {order?.time && (
                    <div className="item">
                      <span>
                        <AccessTimeIcon className="item-icon" />
                        وقت الزيارة
                      </span>
                      <span>
                        {order.time.from} - {order.time.to}
                      </span>
                    </div>
                  )}

                  {order?.lastStatus && (
                    <div className="item">
                      <span>الحالة الحالية</span>
                      <span className="status-badge">
                        {getStatusLabel(order.lastStatus.value)}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                // Regular Invoice
                <div className="total">
                  <p className="title">الفاتورة</p>

                  {order?.services?.map((item, idx) => {
                    const serviceName =
                      item?.service?.name?.ar || item?.name?.ar || "غير متوفر";
                    const servicePrice =
                      item?.service?.price || item?.price || 0;
                    const quantity = item?.quantity || 1;
                    const total = servicePrice * quantity;

                    return (
                      <div key={item._id || idx} className="item">
                        <span>
                          {serviceName}
                          {quantity > 1 && ` (× ${quantity})`}
                        </span>
                        <span>{total} ريال</span>
                      </div>
                    );
                  })}

                  {order?.promoCode?.discount && (
                    <div
                      className="item"
                      style={{
                        borderTop: "1px solid #ccc",
                        paddingTop: "8px",
                      }}
                    >
                      <span>الخصم</span>
                      <span className="discount-text">
                        {order.promoCode.discount}- ريال
                      </span>
                    </div>
                  )}

                  <div
                    className="item"
                    style={{
                      borderTop: "1px solid #ccc",
                      paddingTop: "8px",
                      fontWeight: "bold",
                    }}
                  >
                    <span>الاجمالي</span>
                    <span>{order?.totalPrice?.totalPrice || 0} ريال</span>
                  </div>

                  <div className="btn">
                    <ColorButton
                      size="medium"
                      color="#636270"
                      text="طباعة الفاتورة"
                      icon={<PrintIcon />}
                      onClick={handlePrintClick}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bills-container">
            <div className="bill">
              <div className="id"></div>
              <div className="bill-wrapper">
                {/* Hide DataGrid if it's a package or no services */}
                {!order?.isPackage &&
                  order?.services &&
                  order.services.length > 0 && (
                    <div className="data-grid">
                      <DataGrid
                        sx={{ backgroundColor: "white", width: "auto" }}
                        autoHeight={true}
                        columns={OrderColumns}
                        rows={order.services.map((row, index) => ({
                          ...row,
                          index: index + 1,
                          id: row._id || index + 1,
                        }))}
                        disableVirtualization
                        disableColumnFilter
                        disableColumnMenu
                        hideFooter
                      />
                    </div>
                  )}

                {/* Show message for package orders */}
                {order?.isPackage && (
                  <div className="package-message-box">
                    <div className="package-icon">📦</div>
                    <h3>هذا الطلب تابع للاشتراك</h3>
                    <p>الخدمات المقدمة مشمولة ضمن باقة الاشتراك الخاصة بك</p>
                    {order?.notes && (
                      <div className="package-notes">
                        <strong>تفاصيل الطلب:</strong>
                        <p>{order.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="status">
                <Stepper statusArray={order?.status || []} />
              </div>
            </div>
          </div>

          <div className="order-images">
            <p className="title">صور الطلب</p>

            <div className="images-container">
              {/* Technician Arrived Image */}
              <div className="img-container">
                <p>وصول الفني</p>
                <img
                  src={getStatusImage("technicianArrived")}
                  alt="وصول الفني"
                  loading="lazy"
                />
              </div>

              {/* In Progress Image */}
              <div className="img-container">
                <p>قيد التنفيذ</p>
                <img
                  src={getStatusImage("inProgress")}
                  alt="قيد التنفيذ"
                  loading="lazy"
                />
              </div>

              {/* Completed Image */}
              <div className="img-container">
                <p>مكتمل</p>
                <img
                  src={getStatusImage("completed")}
                  alt="مكتمل"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* Print Bill Modal - Only show if not a package */}
          {!order?.isPackage && (
            <PrintBillModal
              open={openPrintModal}
              onClose={handleClosePrintModal}
              order={order}
            />
          )}
        </div>
      )}

      <LocationMapModal
        open={mapModalOpen}
        onClose={() => setMapModalOpen(false)}
        address={selectedAddress}
      />
    </>
  );
};

export default ViewProductOrder;
